import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  Component,
  DestroyRef,
  effect,
  inject,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { Navbar } from './components/navbar/navbar';
import { Footer } from './components/footer/footer';
import { CookieConsent } from './components/cookie-consent/cookie-consent';
import { CanonicalService } from './shared/canonical.service';
import { JsonLdService } from './shared/json-ld.service';
import { OpenGraphService } from './shared/open-graph.service';
import { AccountAuthService } from './shared/account-auth.service';

const DESTRUCTIVE_OPS_PERMISSIONS = new Set([
  'rotate_key_encryption_key',
  'force_fresh_start',
  'revoke_api_keys',
  'edit_account_roles',
  'edit_own_account_roles',
]);

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterOutlet,
    Navbar,
    Footer,
    CookieConsent,
  ],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  private readonly fb = inject(FormBuilder);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly auth = inject(AccountAuthService);
  private readonly canonical = inject(CanonicalService);
  // Instantiating this service enables automatic route-based OG/Twitter tags.
  private readonly openGraph = inject(OpenGraphService);
  // Route-aware JSON-LD (@graph: Organization, WebSite, Service, WebPage, BreadcrumbList).
  private readonly jsonLd = inject(JsonLdService);

  protected readonly layerDeep = signal('translate3d(0, 0, 0)');
  protected readonly layerMid = signal('translate3d(0, 0, 0)');
  /** False on bare document routes (e.g. consent popup privacy policy tab). */
  protected readonly showSiteChrome = signal(!this.routeHasBareChrome());
  protected readonly destructiveOpsSetupRequired = signal(false);
  protected readonly destructiveOpsSetupChecking = signal(false);
  protected readonly destructiveOpsSetupSubmitting = signal(false);
  protected readonly destructiveOpsSetupError = signal('');
  protected readonly destructiveOpsSetupForm = this.fb.nonNullable.group({
    operationsPassword: ['', [Validators.required, Validators.minLength(8)]],
    confirmOperationsPassword: [
      '',
      [Validators.required, Validators.minLength(8)],
    ],
  });

  private checkedDestructiveOpsSessionKey = '';
  private destructiveOpsStatusRequestActive = false;

  constructor() {
    this.canonical.setCanonical();
    this.openGraph.updateOpenGraph();

    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.showSiteChrome.set(!this.routeHasBareChrome());
      });

    effect(() => {
      const session = this.auth.session();

      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      if (!session) {
        this.checkedDestructiveOpsSessionKey = '';
        this.destructiveOpsSetupRequired.set(false);
        this.destructiveOpsSetupError.set('');
        return;
      }

      const permissions = session.user.permissions ?? [];
      const needsDestructiveOpsPassword = permissions.some((permission) =>
        DESTRUCTIVE_OPS_PERMISSIONS.has(permission)
      );

      if (!needsDestructiveOpsPassword) {
        this.destructiveOpsSetupRequired.set(false);
        this.destructiveOpsSetupError.set('');
        return;
      }

      const sessionKey = `${session.user.uuid}:${session.createdAt}`;
      if (
        this.checkedDestructiveOpsSessionKey === sessionKey ||
        this.destructiveOpsStatusRequestActive
      ) {
        return;
      }

      this.checkedDestructiveOpsSessionKey = sessionKey;
      this.checkDestructiveOpsPasswordStatus();
    });

    afterNextRender(() => {
      if (!isPlatformBrowser(this.platformId)) {
        return;
      }

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      let scheduled = false;
      const applyParallax = () => {
        const y = window.scrollY || document.documentElement.scrollTop;
        this.layerDeep.set(`translate3d(0, ${y * 0.14}px, 0)`);
        this.layerMid.set(`translate3d(0, ${y * -0.09}px, 0)`);
      };

      const onScroll = () => {
        if (!scheduled) {
          scheduled = true;
          requestAnimationFrame(() => {
            scheduled = false;
            applyParallax();
          });
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      applyParallax();

      this.destroyRef.onDestroy(() =>
        window.removeEventListener('scroll', onScroll)
      );
    });
  }

  protected submitDestructiveOpsSetup() {
    if (
      this.destructiveOpsSetupForm.invalid ||
      this.destructiveOpsSetupSubmitting()
    ) {
      this.destructiveOpsSetupForm.markAllAsTouched();
      return;
    }

    const { operationsPassword, confirmOperationsPassword } =
      this.destructiveOpsSetupForm.getRawValue();
    if (operationsPassword !== confirmOperationsPassword) {
      this.destructiveOpsSetupError.set('The operations passwords do not match.');
      return;
    }

    this.destructiveOpsSetupSubmitting.set(true);
    this.destructiveOpsSetupError.set('');
    this.auth
      .createDestructiveOpsPassword(operationsPassword)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.destructiveOpsSetupSubmitting.set(false);
          this.destructiveOpsSetupRequired.set(false);
          this.destructiveOpsSetupForm.reset();
        },
        error: (message: string) => {
          this.destructiveOpsSetupSubmitting.set(false);
          this.destructiveOpsSetupError.set(message);
        },
      });
  }

  private routeHasBareChrome(): boolean {
    let route = this.router.routerState.snapshot.root;
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route.data['bareChrome'] === true;
  }

  private checkDestructiveOpsPasswordStatus() {
    this.destructiveOpsStatusRequestActive = true;
    this.destructiveOpsSetupChecking.set(true);
    this.auth
      .getDestructiveOpsPasswordStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.destructiveOpsStatusRequestActive = false;
          this.destructiveOpsSetupChecking.set(false);
          this.destructiveOpsSetupRequired.set(!!response.data?.requiresSetup);
          this.destructiveOpsSetupError.set('');
        },
        error: () => {
          this.destructiveOpsStatusRequestActive = false;
          this.destructiveOpsSetupChecking.set(false);
        },
      });
  }
}
